/**
 * Reusable JSON code block modal component for displaying formatted JSON data.
 */

import { useState, useMemo, useEffect } from 'react';
import { Box, Modal, ModalInset, Stack, CodeBlock, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { CODE_BLOCK_THEME } from '../../constants/code-block-theme';

export interface JsonCodeBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  code: string;
  skeletonRows?: number;
}

export function JsonCodeBlockModal({ isOpen, onClose, title, code, skeletonRows = 10 }: JsonCodeBlockModalProps) {
  const [isReady, setIsReady] = useState(false);
  const codeBlockId = useMemo(() => `json-modal-${Math.random().toString(36).slice(2, 11)}`, []);

  // Use effect to detect when modal opens and prepare CodeBlock
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame for next paint
      const frameId = requestAnimationFrame(() => setIsReady(true));
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReady(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <ModalInset overflow="hidden">
        {/* Screen reader description */}
        <div id={`${codeBlockId}-description`} style={{ position: 'absolute', left: '-9999px' }}>
          JSON data viewer with syntax highlighting and copy functionality
        </div>
        <Box aria-describedby={`${codeBlockId}-description`}>
          {!isReady ? (
            <Box
              py="100"
              px="150"
              style={{
                minHeight: '200px',
                maxHeight: '600px',
                backgroundColor: CODE_BLOCK_THEME.background,
              }}
            >
              <Stack gap="075">
                {Array.from({ length: skeletonRows }).map((_, index) => (
                  <SkeletonShape
                    key={index}
                    overflow="hidden"
                    style={{
                      height: '16px',
                      width: index % 3 === 0 ? '100%' : index % 3 === 1 ? '85%' : '70%',
                      borderRadius: themeVars.radius['2'],
                      backgroundColor: CODE_BLOCK_THEME.skeletonHighlight,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <CodeBlock
              code={code}
              language="json"
              withLineFoldingControls={true}
              withCopyButton={true}
              withLineNumbers={true}
              mode="dark"
              darkTheme="night-owl"
              isRounded={false}
              style={{
                minHeight: '200px',
                maxHeight: '600px',
              }}
            />
          )}
        </Box>
      </ModalInset>
    </Modal>
  );
}
